import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { emailLightReachCreditApplication, verifyLightReachUser } from "../../dtos/lightreach";
import { CircularProgress } from "@material-ui/core";

/**
 * Modal shown when Apply is clicked for LightReach. Displays a confirmation
 * that the credit application will be emailed, then on "Email Credit Application"
 * calls the LightReach API and shows success or error message.
 * "Open Credit Application" is shown only after verify_user returns success.
 */
export default function LightReachApplyModal({
  show,
  onHide,
  firstName,
  email,
  acctId,
  repEmail,
}) {
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [error, setError] = useState(null);
  const [canShowOpenCreditApp, setCanShowOpenCreditApp] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  useEffect(() => {
    if (!show || !repEmail) {
      setCanShowOpenCreditApp(false);
      return;
    }
    let cancelled = false;
    setVerifyLoading(true);
    verifyLightReachUser(repEmail)
      .then((response) => {
        if (!cancelled) {
          const success = response?.success === true || response?.Success === true;
          setCanShowOpenCreditApp(!!success);
        }
      })
      .catch(() => {
        if (!cancelled) setCanShowOpenCreditApp(false);
      })
      .finally(() => {
        if (!cancelled) setVerifyLoading(false);
      });
    return () => { cancelled = true; };
  }, [show, repEmail]);

  const handleEmailCreditApplication = async () => {
    if (!acctId || !email) return;
    setStatus("sending");
    setError(null);
    try {
      const response = await emailLightReachCreditApplication({
        firstName: firstName || "",
        acctId,
        email,
      });
      const success = response?.success === true || response?.Success === true;
      setStatus(success ? "success" : "error");
    } catch (err) {
      setError(err?.message);
      setStatus("error");
    }
  };

  const handleClose = () => {
    setStatus(null);
    setError(null);
    setCanShowOpenCreditApp(false);
    onHide();
  };

  const displayName = firstName ? `${firstName} ` : "";
  const isResult = status === "success" || status === "error";
  const creditAppUrl = acctId
    ? `https://sabal.palmetto.com/accounts/${acctId}/application/create`
    : null;

  const handleOpenCreditApp = () => {
    if (creditAppUrl) window.open(creditAppUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>LightReach Credit Application</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isResult && (
          <p>
            {displayName}will be sent a credit application to: <span className="text-primary">{email}</span>. If this
            is incorrect, please close this window and update the customer's
            email address before you continue.
          </p>
        )}
        {status === "sending" && (
          <div className="d-flex align-items-center gap-2">
            <CircularProgress size={20} />
            <span>Sending…</span>
          </div>
        )}
        {status === "success" && (
          <p>
            The credit application was emailed to: <span className="text-primary">{email}</span>. Once credit is
            approved, refresh the status to generate and send the contract.
          </p>
        )}
        {(status === "error" || error) && (
          <p className="text-danger">
            There was an error sending this to the customer. Try again or
            contact Sales Support.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!isResult && (
          <>
            {verifyLoading && (
              <span className="me-2 align-self-center">
                <CircularProgress size={20} />
              </span>
            )}
            {canShowOpenCreditApp && !verifyLoading && (
              <Button
                variant="primary"
                className="btn-primary"
                onClick={handleOpenCreditApp}
                disabled={!creditAppUrl}
              >
                Open Credit Application
              </Button>
            )}
            <Button
              variant="primary"
              className="btn-primary"
              onClick={handleEmailCreditApplication}
              disabled={status === "sending" || !acctId || !email}
            >
              {status === "sending" ? (
                <>
                  <CircularProgress size={16} color="inherit" className="me-1" />
                  Sending…
                </>
              ) : (
                "Email Credit Application"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
